import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Check, ListChecks } from "lucide-react";
import type { WorkflowRequirement } from "../core";
import { useRuntime } from "../react/runtime/RuntimeContext";

// CockpitChecklist — the cockpit <checklist> card (form-view revamp Enh 14 ·
// Stage 3B, backed by workflow Enhancement 02).
//
// Renders the current stage's requirement items + an N-of-M progress bar +
// a "blocks <next step>" indicator when blocking items are unmet. MANUAL items
// are tickable (dispatch the audited complete/reopen commands); AUTO items are
// read-only (they reflect the record expression live). Renders only on a saved
// record; silently omits when the model has no workflow requirements.

interface ChecklistCardProps {
    modelKey: string;
    recordRef: string | null;
}

export function CockpitChecklist({ modelKey, recordRef }: ChecklistCardProps): ReactNode {
    const runtime = useRuntime();
    const [items, setItems] = useState<WorkflowRequirement[]>([]);
    const [blocked, setBlocked] = useState(false);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [epoch, setEpoch] = useState(0);

    useEffect(() => {
        if (!recordRef) {
            setLoading(false);
            return;
        }
        let alive = true;
        void (async () => {
            setLoading(true);
            try {
                const res = await runtime.services.workflow.requirements(modelKey, recordRef);
                if (!alive) return;
                setItems(res.requirements ?? []);
                setBlocked(Boolean(res.blocked));
            } catch {
                if (alive) {
                    setItems([]);
                    setBlocked(false);
                }
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [runtime, modelKey, recordRef, epoch]);

    if (!recordRef) return null;
    if (!loading && items.length === 0) return null; // no requirements on this stage → omit

    const total = items.length;
    const done = items.filter((i) => i.satisfied).length;
    const pct = total ? Math.round((done / total) * 100) : 0;

    const toggle = async (item: WorkflowRequirement) => {
        if (busy || item.kind !== "manual") return;
        setBusy(true);
        try {
            if (item.satisfied) {
                await runtime.services.workflow.reopenRequirement(item.requirement_id, recordRef);
            } else {
                await runtime.services.workflow.completeRequirement(item.requirement_id, recordRef);
            }
            setEpoch((e) => e + 1);
        } catch {
            /* surfaced by the platform error toast */
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="cockpit-card cockpit-card--checklist">
            <div className="cockpit-card__head">
                <ListChecks size={13} />
                Checklist
            </div>
            <div className="cockpit-card__body">
                {loading ? (
                    <p className="cockpit-tasks__empty">Loading…</p>
                ) : (
                    <>
                        <div className="ck-list">
                            {items.map((item) => {
                                const manual = item.kind === "manual";
                                const tag = item.satisfied ? null : !manual ? "Auto" : item.blocks ? "Blocks" : "Pending";
                                return (
                                    <div
                                        key={item.requirement_id}
                                        className={`ck-item${item.satisfied ? " ck-item--done" : ""}`}
                                    >
                                        <button
                                            type="button"
                                            className={`gtd-check${item.satisfied ? " gtd-check--done" : ""}`}
                                            onClick={() => toggle(item)}
                                            disabled={busy || !manual}
                                            title={manual ? (item.satisfied ? "Reopen" : "Mark done") : "Computed automatically"}
                                        >
                                            {item.satisfied ? <Check size={12} /> : null}
                                        </button>
                                        <span className="ck-text" title={item.help || undefined}>{item.name}</span>
                                        {tag ? <span className="ck-tag">{tag}</span> : null}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="ck-progress">
                            {done} of {total}{blocked ? " · blocks handover" : ""}
                            <span className="ck-bar"><span className="ck-bar__fill" style={{ width: `${pct}%` }} /></span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
